import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/dates";
import { ROSTER_STATUS } from "@/lib/colors";
import { Pill } from "@/components/common";
import { RosterEditor, toPayload } from "@/components/roster/RosterEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const RosterReviewDialog = ({ roster, manager, onClose, onDone }) => {
  const [shifts, setShifts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (roster) {
      setShifts(roster.shifts);
      setDirty(false);
    }
  }, [roster]);

  if (!roster) return null;

  const act = async (approve) => {
    setBusy(true);
    try {
      if (dirty) await api.put(`/rosters/${roster.id}`, { shifts: toPayload(shifts) });
      if (approve) await api.post(`/rosters/${roster.id}/approve`);
      toast.success(approve ? "Schedule approved" : "Changes saved — manager notified");
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const st = ROSTER_STATUS[roster.status];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="roster-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#24474A] flex items-center gap-3 flex-wrap">
            {manager?.name} <Pill cls={st.cls}>{st.label}</Pill>
          </DialogTitle>
          <DialogDescription>Week of {fmt(roster.week_start, "d MMM yyyy")} · {manager?.department}. Edit shifts directly — the manager is notified of every change.</DialogDescription>
        </DialogHeader>
        <RosterEditor weekStart={roster.week_start} shifts={shifts} disabled={busy} onChange={(s) => { setShifts(s); setDirty(true); }} />
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-full" onClick={() => act(false)} disabled={busy || !dirty} data-testid="review-save-changes-btn"><Save className="w-4 h-4 mr-1.5" /> Save changes</Button>
          <Button className="rounded-full" onClick={() => act(true)} disabled={busy} data-testid="review-approve-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" /> {dirty ? "Save & approve" : "Approve"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
